// registrationHandlers.js
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { assignUserRole } from "../../../../components/utils/roleManagement";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import { getFunctions, httpsCallable } from "firebase/functions";

// Sign in with Google
// Sign in with Google
export const onGoogleSignIn = async ({
  auth,
  assignUserRole,
  dispatch,
  setUser,
  setError,
  setSuccess,
  setShowGoogleMessage,
  companyName,
  navigate,
  setShowErrorScreen,
}) => {
  setError(""); // Clear any existing errors
  setSuccess(""); // Clear any existing success messages
  setShowGoogleMessage(true); // Show Google-specific UI messaging

  try {
    const functions = getFunctions();
    const checkDuplicate = httpsCallable(functions, "checkDuplicate");

    // Initialize Google Auth Provider
    const provider = new GoogleAuthProvider();

    // Sign in the user with Google
    const userCredential = await signInWithPopup(auth, provider);
    const googleUser = userCredential.user; // Initialize googleUser here

    // Display success message with the user's display name
    setSuccess(`Welcome, ${googleUser.displayName}!`);

    // Dispatch user information to the Redux store
    dispatch(
      setUser({
        uid: googleUser.uid,
        email: googleUser.email,
        name: googleUser.displayName,
      })
    );

    // Assign the custom role to the user based on the company name
    const customRole = `Hero-${companyName}-TechPerformanceCentre`;
    console.log(
      "[RegisterFunctions] Google Sign-In - Custom role:",
      customRole
    );
    await assignUserRole(googleUser.uid, customRole);

    // 2) Force the token refresh
    await googleUser.getIdToken(true);
    const newGoogleToken = await googleUser.getIdTokenResult();
    console.log("New token claims:", newGoogleToken.claims);

    // Generate unique IDs for company and brand
    const companyId = uuidv4(); // Unique ID for the company
    const brandId = uuidv4(); // Unique ID for the brand

    // Prepare Firestore operation payloads
    const organisationData = {
      companyId, // Unique company ID
      name: companyName, // Organisation name
      description: `${companyName} main organisation`,
      brands: [{ brandId, brandName: "TechPerformanceCentre" }],
    };

    const sharedBrandData = {
      brandId,
      name: "TechPerformanceCentre", // Descriptive brand name
      description: "The organisation itself.",
      userIds: [googleUser.uid], // Add the superAdmin user to the TechPerformanceCentre brand
      companyRegistrationNumber: "", // Placeholder, can be updated later
      bankDetails: {}, // Placeholder, can be updated later
    };

    const userMasterData = {
      userId: googleUser.uid,
      name: googleUser.displayName,
      email: googleUser.email,
      role: "superAdmin", // SuperAdmin role for organisation creator
      companyId,
      companyName: companyName,
      brandId,
      brandName: "TechPerformanceCentre", // Descriptive brand name
      status: "emailVerified", // Google sign-in assumes verified email
      createdAt: new Date().toISOString(), // Record creation time
    };

    // Call Cloud Function to perform operations
    const organisationBrandUserOperations = httpsCallable(
      functions,
      "organisationBrandUserOperations"
    );

    // Write to Firestore
    await organisationBrandUserOperations({
      operationType: "create",
      entityType: "organisation",
      entityData: organisationData,
    });

    await organisationBrandUserOperations({
      operationType: "create",
      entityType: "brand",
      entityData: {
        ...sharedBrandData,
        companyId,
      },
    });

    await organisationBrandUserOperations({
      operationType: "create",
      entityType: "user",
      entityData: userMasterData,
    });

    // Navigate to the next step
    navigate(
      "/registerHeroUserConsole",
      `${companyName}-TechPerformanceCentre`
    );
  } catch (error) {
    // Handle errors and reset UI messaging
    setError(error.message);
    setShowGoogleMessage(false);
    setShowErrorScreen(true);
  }
};

export const onEmailSignUp = async ({
  e,
  email,
  password,
  name,
  companyName,
  auth,
  setError,
  setSuccess,
  setLoading,
  setCurrentUser,
  dispatch,
  setUser,
  setShowErrorScreen,
}) => {
  e.preventDefault(); // Prevent default form submission
  setError(""); // Clear any existing error messages
  setSuccess(""); // Clear any existing success messages

  setLoading(true); // Show the loading spinner

  try {
    const functions = getFunctions();

    // Create the user with email and password
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Assign a role based on the companyName
    const customRole = `Hero-${companyName}-TechPerformanceCentre`;
    console.log("[RegisterFunctions] Email Sign-Up - Custom role:", customRole);
    console.log(
      "[RegisterFunctions] User will be saved to: /TPC_Group/TechPerformanceCentre/HeroUsers"
    );

    // Update success message
    setSuccess(`Welcome, ${name}!`);

    // Set the current user in state
    setCurrentUser(user);

    // Dispatch user info to Redux store
    dispatch(
      setUser({
        uid: user.uid,
        email: user.email,
        name: name,
      })
    );

    // Assign the role to the user and send a verification email
    await assignUserRole(user.uid, customRole);
    await sendEmailVerification(user);

    await user.getIdToken(true);
    const newToken = await user.getIdTokenResult();
    console.log("New token claims:", newToken.claims);

    // Generate unique IDs for company and brand
    const companyId = uuidv4(); // Unique ID for the company
    const brandId = uuidv4(); // Unique ID for the brand

    // Prepare Firestore operation payloads
    const organisationData = {
      companyId, // Unique company ID
      name: companyName, // Organisation name
      description: `${companyName} main organisation`,
      brands: [{ brandId, brandName: "TechPerformanceCentre" }],
    };

    const sharedBrandData = {
      brandId,
      name: "TechPerformanceCentre", // Descriptive brand name
      description: "The organisation itself.",
      userIds: [user.uid], // Add the superAdmin user to the TechPerformanceCentre brand
      companyRegistrationNumber: "", // Placeholder, can be updated later
      bankDetails: {}, // Placeholder, can be updated later
    };

    const userMasterData = {
      userId: user.uid,
      name,
      email,
      role: "superAdmin", // SuperAdmin role for organisation creator
      companyId,
      companyName: companyName,
      brandId,
      brandName: "TechPerformanceCentre", // Descriptive brand name
      status: "emailUnverified",
      createdAt: new Date().toISOString(), // Record creation time
    };

    // Call Cloud Function to perform operations
    const organisationBrandUserOperations = httpsCallable(
      functions,
      "organisationBrandUserOperations"
    );

    // Write to Firestore
    await organisationBrandUserOperations({
      operationType: "create",
      entityType: "organisation",
      entityData: organisationData,
    });

    await organisationBrandUserOperations({
      operationType: "create",
      entityType: "brand",
      entityData: {
        ...sharedBrandData,
        companyId,
      },
    });

    await organisationBrandUserOperations({
      operationType: "create",
      entityType: "user",
      entityData: userMasterData,
    });

    // Optionally navigate to the next step or update the UI
  } catch (error) {
    setError(error.message); // Handle and display errors
    setShowErrorScreen(true);
  } finally {
    setLoading(false); // Hide the loading spinner
  }
};
